import React from "react";
import { Typography, Grid } from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import { useSelector } from "react-redux";
import { getAppColor } from "@icarius-app/selectors";
import { formatNumberOrReturnUndefined } from "@icarius-utils/format";
import useIsSocietyCountry from "@icarius-app/components/wrapper/hooks/useIsSocietyCountry";

const Item = ({ title, value, hasLine, hasColor, isLastItem }) => {
    const color = useSelector(getAppColor);

    return (
        <Grid container item xs={12} alignItems="flex-end" style={{ marginTop: isLastItem ? -20 : 0 }}>
            <Grid
                container
                item
                xs={9}
                alignItems="center"
                style={{ borderBottom: hasLine ? `1px solid var(--mainText)` : "" }}
            >
                <Typography
                    style={{
                        fontSize: "1.2rem",
                        lineHeight: "1.4",
                        color: hasColor ? color : "var(--mainText)",
                        fontWeight: hasColor ? 700 : 400,
                    }}
                    variant="subtitle1"
                >
                    {title}
                </Typography>
            </Grid>
            <Grid
                container
                alignItems="center"
                justify="flex-end"
                item
                xs={3}
                style={{ borderBottom: hasLine ? `1px solid var(--mainText)` : "" }}
            >
                <Typography
                    style={{
                        fontSize: "1.2rem",
                        lineHeight: "1.4",
                        color: hasColor ? color : "var(--mainText)",
                        fontWeight: hasColor ? 700 : 400,
                    }}
                    variant="subtitle1"
                >
                    {formatNumberOrReturnUndefined(value, 0, 2)}
                </Typography>
            </Grid>
        </Grid>
    );
};

const BalanceData = ({ data }) => {
    const isPeru = useIsSocietyCountry("PE");

    return (
        <Grid container item xs={12} style={{ height: "65vh" }}>
            {isPeru ? (
                <>
                    <Grid container item xs={12} style={{ padding: 10 }}>
                        <Item title={getLocalizedString("totalEarnedDays")} value={data.credit_total_days} />
                        <Item title={getLocalizedString("totalUsedDays")} value={data.debit_used_days} />
                        <Item
                            title={getLocalizedString("totalDaysToBeUsedInTheFuture")}
                            value={data.credit_unused_days}
                        />
                        <Item title={getLocalizedString("totalTruncated")} value={data.truncated} />
                        <Item title={getLocalizedString("totalPending")} value={data.pending} />
                        <Item title={getLocalizedString("totalOverdue")} value={data.expired} hasLine />
                        <Item
                            isLastItem
                            title={getLocalizedString("totalAvailableBalance")}
                            value={Number(data.truncated) + Number(data.pending) + Number(data.expired)}
                            hasColor
                        />
                    </Grid>
                    <Grid container item xs={12} style={{ padding: 10 }}>
                        <Item
                            title={getLocalizedString("totalPlusUnusedAvailableBalance")}
                            value={
                                Number(data.truncated) +
                                Number(data.pending) +
                                Number(data.expired) -
                                Number(data.credit_unused_days)
                            }
                        />
                    </Grid>
                </>
            ) : (
                <>
                    <Grid container item xs={12} style={{ padding: 10 }}>
                        <Item
                            title={getLocalizedString("totalLegalAndOtherDays")}
                            value={data.credit_total_legal_days + data.credit_total_other_days}
                        />
                        <Item
                            title={getLocalizedString("totalAdditionalOrProgressiveDays")}
                            value={data.credit_total_additional_days}
                            hasLine
                        />
                        <Item title={getLocalizedString("totalEarnedDays")} value={data.credit_total_days} />
                    </Grid>
                    <Grid container item xs={12} style={{ padding: 10 }}>
                        <Item title={getLocalizedString("usedLegalDays")} value={data.debit_used_legal_days} />
                        <Item
                            title={getLocalizedString("additionalOrProgressiveUsedDays")}
                            value={data.debit_used_additional_days}
                            hasLine
                        />
                        <Item title={getLocalizedString("totalUsedDays")} value={data.debit_used_days} />
                    </Grid>
                    <Grid container item xs={12} style={{ padding: 10 }}>
                        <Item
                            title={getLocalizedString("daysToUseInFutureLegalAndOther")}
                            value={data.credit_total_unused_legal_days}
                        />
                        <Item
                            title={getLocalizedString("daysToUseInFutureAdditionalOrProgressive")}
                            value={data.credit_total_unused_additional_days}
                            hasLine
                        />
                        <Item
                            title={getLocalizedString("totalDaysToBeUsedInTheFuture")}
                            value={data.credit_unused_days}
                        />
                    </Grid>
                    <Grid container item xs={12} style={{ padding: 10 }}>
                        <Item
                            title={getLocalizedString("legalOthersBalance")}
                            value={data.debit_legal_available_days}
                        />
                        <Item
                            title={getLocalizedString("additionalOrProgressiveBalance")}
                            value={data.debit_additional_available_days}
                            hasLine
                        />
                        <Item
                            title={getLocalizedString("totalAvailableBalance")}
                            value={data.debit_total_available_days}
                            hasColor
                        />
                    </Grid>
                    <Grid container item xs={12} style={{ padding: 10 }}>
                        <Item
                            title={getLocalizedString("totalPlusUnusedAvailableBalance")}
                            value={data.debit_total_available_days + data.credit_unused_days}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default BalanceData;
